import {ReactNode} from 'react';

import {ColumnConstraint, TooltipIcon} from 'notebook/tables/constants/TableColumnConstants';
import {TABLE_COLUMN, TableDimension} from 'notebook/tables/constants/TableSelectionConstants';
import {
  computeColumnHeaderIconPrefix,
  computeColumnIsOutdated,
  computeHasDestinationColumnMapping,
  computeIsSingleInputColumn,
  computeShouldHighlightColumnHeader,
} from 'notebook/tables/store/logic/ColumnHeaderLogic';
import {
  AxisHeaderModel,
  createAxisHeaderIdentitySelector,
} from 'notebook/tables/store/models/AxisHeaderModel';
import {TableModel} from 'notebook/tables/store/models/TableModel';
import {createDebuggableSelector} from 'notebook/tables/store/selectors/util/SelectorCreators';
import {TableEditableStore} from 'notebook/tables/store/TableStore';
import TableUtil from 'notebook/tables/TableUtil';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

/**
 * The model for a single column header cell, keyed by column index. Rendered by `TableHeaderRow`
 * for regular columns and by `TableSingleInput` for Single Input columns.
 *
 * This is the per-column counterpart to `RowModel`. Not to be confused with `TableColumnHeaderModel`
 * (`TableModel.columnHeader`), which holds the table-level configuration that every column header
 * shares.
 */
export interface ColumnHeaderModel extends AxisHeaderModel {
  dim: TableDimension.COLUMN;
  /** The editable name of the column, which renaming starts from. */
  label: string | null;
  /** The icon rendered before the label, or null when the column has no prefix icon. */
  iconPrefix: BenchlingIconName | null;
  /**
   * Icons rendered after the label, whose tooltips are also folded into the cell's tooltip. Only
   * supplied by standalone column headers; in-table columns take these from their column constraint.
   */
  tooltipIcons: Array<TooltipIcon> | null;
  /** Whether the header is highlighted as a user-added custom column. */
  shouldHighlightCell: boolean;
  /** Whether the column is backed by an outdated schema field. */
  columnIsOutdated: boolean;
}

/** Creates a selector for the column header at `index` against the table store. */
export function createColumnHeaderModelSelector(
  tableSelector: (store: TableEditableStore) => TableModel,
  index: number
): (store: TableEditableStore) => ColumnHeaderModel {
  const columnConstraintSelector = (store: TableEditableStore): ColumnConstraint | null =>
    tableSelector(store).props.table.columnConstraints?.[index] ?? null;

  // Each input is a value (or a stable object off table props) rather than the table itself, so
  // that editing an unrelated part of the table does not invalidate every column header's model.
  return createDebuggableSelector.withTypes<TableEditableStore>()(
    [
      createAxisHeaderIdentitySelector(tableSelector, TABLE_COLUMN, index),
      (store) => TableUtil.getRenderedColumnLabel(tableSelector(store).props.table, index),
      (store) => TableUtil.getColumnLabel(tableSelector(store).props.table, index),
      (store) => columnConstraintSelector(store)?.columnDefinition ?? null,
      (store) => TableUtil.getTableKind(tableSelector(store).props.table),
      (store) => tableSelector(store).singleInputs.boundaryIndex,
      (store) => store.editorContext.highlightCustomColumns,
      (store) => tableSelector(store).props.hideColumnHeaderIcons,
      // The column mapping lookup reads global mappings state, so it is an input rather than part
      // of the memoized result.
      (store) =>
        computeHasDestinationColumnMapping(
          tableSelector(store).props.fileId,
          tableSelector(store).props.table,
          columnConstraintSelector(store)
        ),
    ],
    (
      identity,
      customLabel,
      label,
      columnDefinition,
      tableKind,
      singleInputBoundaryIndex,
      highlightCustomColumns,
      hideColumnHeaderIcons,
      hasDestinationColumnMapping
    ): ColumnHeaderModel => ({
      ...identity,
      dim: TABLE_COLUMN,
      isHeaderCell: true,
      customLabel,
      label,
      iconPrefix: computeColumnHeaderIconPrefix({
        hideColumnHeaderIcons,
        hasDestinationColumnMapping,
        columnDefinition,
      }),
      tooltipIcons: null,
      shouldHighlightCell: computeShouldHighlightColumnHeader({
        isSingleInputColumn: computeIsSingleInputColumn(index, singleInputBoundaryIndex),
        highlightCustomColumns,
        columnDefinition,
        tableKind,
      }),
      columnIsOutdated: computeColumnIsOutdated(columnDefinition),
    })
  );
}

/**
 * A {@link ColumnHeaderModel} for a column header rendered outside of any table store (for example
 * `SimpleTableAxisCell`). Such headers only ever label real columns, so they are never ghost cells,
 * and their label and icons come directly from their props rather than from a table.
 */
export function makeStandaloneColumnHeaderModel({
  index,
  customLabel = null,
  label = null,
  tooltipIcons = null,
}: {
  index: number;
  customLabel?: ReactNode;
  label?: string | null;
  tooltipIcons?: Array<TooltipIcon> | null;
}): ColumnHeaderModel {
  return {
    dim: TABLE_COLUMN,
    index,
    isGhostCell: false,
    isHeaderCell: true,
    customLabel,
    label,
    iconPrefix: null,
    tooltipIcons,
    shouldHighlightCell: false,
    columnIsOutdated: false,
  };
}
